import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient } from '@/lib';
import type { GetExperienceInput } from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import { PageExperienceFragment } from '../fragments/PageExperience';
import type { PageExperience } from '../types';
import type {
  GetPageExperienceQuery,
  GetPageExperienceQueryVariables,
} from './useGetPageExperience.generated';

const query = gql`
  ${PageExperienceFragment}
  query GetPageExperience($input: GetExperienceInput!) {
    getExperience(input: $input) {
      ...PageExperience
    }
  }
`;

const selector = (data: GetPageExperienceQuery): PageExperience => {
  return data.getExperience;
};

type Options = {
  retryOnMount?: boolean;
};

export const useGetPageExperience = (
  experienceId: string | undefined,
  options?: Options
) => {
  const queryKey = createExperienceQueryKey(experienceId);

  return useQuery<GetPageExperienceQuery, QueryError, PageExperience>({
    ...options,
    enabled: Boolean(experienceId),
    queryKey,
    select: selector,
    // Keep the previously loaded experience visible while switching to another
    // child so the loader doesn't flash through its "loading" state. That flash
    // strands the re-entering "loaded" fade at opacity 0 (old content fades out,
    // new content never fades in) — only reproducible when the next child isn't
    // already cached.
    placeholderData: keepPreviousData,
    queryFn: () => {
      const input: GetExperienceInput = {
        experienceId: experienceId!, // protected by the `enabled` setting
      };
      return gqlClient.request<
        GetPageExperienceQuery,
        GetPageExperienceQueryVariables
      >(query, { input });
    },
  });
};
